/** The timers the queue uses; a test hands in its own. */
export interface TimerApi {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export const REAL_TIMERS: TimerApi = {
  set: (callback, ms) => setTimeout(callback, ms),
  clear: handle => clearTimeout(handle as ReturnType<typeof setTimeout>)
};

/** The time an editor has to take a deletion back (the board: «Отменить» for 10 seconds). */
export const UNDO_TTL_MS = 10_000;

interface Entry<T> {
  value: T;
  handle: unknown;
}

/**
 * The deletions that wait for their ten seconds: nothing is sent for a deletion until its time is up (`commit`), taking it back (`undo`) sends nothing at all.
 * `flush` commits everything at once: the editor leaves the page, or starts an action that would meet the item that is about to go.
 */
export class UndoQueue<T> {
  private readonly entries = new Map<number, Entry<T>>();

  constructor(
    private readonly ttlMs: number,
    private readonly commit: (key: number, value: T) => void,
    private readonly changed: () => void = () => undefined,
    private readonly timers: TimerApi = REAL_TIMERS
  ) {
  }

  get size(): number {
    return this.entries.size;
  }

  /** The keys that wait, in the order they were put in. */
  keys(): number[] {
    return [...this.entries.keys()];
  }

  has(key: number): boolean {
    return this.entries.has(key);
  }

  get(key: number): T | undefined {
    return this.entries.get(key)?.value;
  }

  /** Puts a deletion in; one with the same key starts its time again. */
  add(key: number, value: T): void {
    const old = this.entries.get(key);
    if (old) {
      this.timers.clear(old.handle);
    }
    const handle = this.timers.set(() => this.expire(key), this.ttlMs);
    this.entries.set(key, { value, handle });
    this.changed();
  }

  /** Takes a deletion back: nothing is committed. Gives its value, `undefined` when it is not (or no longer) there. */
  undo(key: number): T | undefined {
    const entry = this.entries.get(key);
    if (!entry) {
      return undefined;
    }
    this.timers.clear(entry.handle);
    this.entries.delete(key);
    this.changed();
    return entry.value;
  }

  /** Commits every deletion that waits, now. */
  flush(): void {
    for (const key of this.keys()) {
      this.expire(key);
    }
  }

  /** Forgets everything without committing (the page is gone and the commits were sent by `flush`). */
  dispose(): void {
    for (const entry of this.entries.values()) {
      this.timers.clear(entry.handle);
    }
    this.entries.clear();
  }

  private expire(key: number): void {
    const entry = this.entries.get(key);
    if (!entry) {
      return;
    }
    this.timers.clear(entry.handle);
    this.entries.delete(key);
    this.changed();
    this.commit(key, entry.value);
  }
}
